import React from "react";
import { AppHeaderActions } from "../components/ui/app-header-actions";

export interface OnboardingShellFooterLink {
  id: string;
  label: string;
  href: string;
}

export interface OnboardingShellLayoutProps {
  children: React.ReactNode;
  brandLabel?: string;
  brandSubtitle?: string;
  currentLanguage: string;
  onLanguageToggle: () => void;
  onSignOut?: () => void;
  signOutLabel?: string;
  footerCopyright?: string;
  footerLinks?: OnboardingShellFooterLink[];
}

export function OnboardingShellLayout({
  children,
  brandLabel = "EnviroHub",
  brandSubtitle,
  currentLanguage,
  onLanguageToggle,
  onSignOut,
  signOutLabel,
  footerCopyright,
  footerLinks = [],
}: OnboardingShellLayoutProps) {
  return (
    <div className="flex min-h-screen flex-col bg-surface text-surface-on">
      <header className="sticky top-0 z-40 border-b border-outline-variant/30 bg-surface">
        <div className="mx-auto flex w-full max-w-7xl items-center justify-between px-4 py-4 md:px-10">
          <div className="flex flex-col">
            <span className="font-headline text-h4 font-bold text-primary">{brandLabel}</span>
            {brandSubtitle && (
              <span className="text-micro uppercase tracking-widest text-surface-on-variant">
                {brandSubtitle}
              </span>
            )}
          </div>

          <div className="flex items-center gap-4">
            <AppHeaderActions
              currentLanguage={currentLanguage}
              onLanguageToggle={onLanguageToggle}
              onSignOut={onSignOut}
              signOutLabel={signOutLabel}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-4 py-8 md:px-10">
        {children}
      </main>

      <footer className="border-t border-outline-variant/30 bg-surface-container-lowest">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center justify-between gap-4 px-4 py-6 text-center md:flex-row md:px-10 md:text-start">
          {footerCopyright && (
            <p className="text-body-sm text-surface-on-variant">{footerCopyright}</p>
          )}
          {footerLinks.length > 0 && (
            <nav className="flex flex-wrap items-center justify-center gap-4 md:justify-end">
              {footerLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  className="text-body-sm text-surface-on-variant transition-colors hover:text-primary"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          )}
        </div>
      </footer>
    </div>
  );
}
